import React, { useEffect, useRef, useState } from 'react';
import { BrainTrainingGameIcon, GameShell, shuffle } from '../shared';
import brainGameLevels from '../../../lib/brainGameLevels';
import { formatGameCopy } from './gameCopy';

const TONE_FREQUENCIES = [294, 440, 659];
const patternKey = (pattern) => pattern.join('-');
const { normalizeBrainGameLevel } = brainGameLevels;

const createToneRound = (level = 1) => {
    const length = level >= 2 ? 4 : 3;
    const pattern = Array.from({ length }, () => Math.floor(Math.random() * 3));
    const optionMap = new Map([[patternKey(pattern), pattern]]);

    while (optionMap.size < 4) {
        const candidate = [...pattern];
        const index = Math.floor(Math.random() * candidate.length);
        candidate[index] = (candidate[index] + 1 + Math.floor(Math.random() * 2)) % 3;
        optionMap.set(patternKey(candidate), candidate);
    }

    return {
        id: `${Date.now()}-${Math.random()}`,
        options: shuffle([...optionMap.values()]),
        pattern,
    };
};

function TonePattern({ pattern }) {
    return (
        <span className="UFOV-brain-games__tone-pattern" aria-hidden="true">
            {pattern.map((tone, index) => <i key={`${tone}-${index}`} style={{ height: `${20 + tone * 15}px` }} />)}
        </span>
    );
}

function ToneOrderGame({ copy = {}, initialLevel, ...props }) {
    const startingLevel = normalizeBrainGameLevel('tone-order', initialLevel);
    const audioContextRef = useRef(null);
    const timerRef = useRef(null);
    const [level, setLevel] = useState(startingLevel);
    const [round, setRound] = useState(() => createToneRound(startingLevel));
    const [stage, setStage] = useState('ready');
    const [playCount, setPlayCount] = useState(0);
    const [choice, setChoice] = useState('');
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const correct = choice && choice === patternKey(round.pattern);

    useEffect(() => () => {
        window.clearTimeout(timerRef.current);
        audioContextRef.current?.close?.();
    }, []);

    const playPattern = async () => {
        if (stage === 'playing' || stage === 'feedback' || (stage === 'answer' && playCount >= 2)) {
            return;
        }

        setChoice('');
        setStage('playing');
        setPlayCount((current) => current + 1);
        window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(
            () => setStage('answer'),
            round.pattern.length * 360 + 180
        );
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;

        if (AudioContextClass) {
            try {
                if (!audioContextRef.current) {
                    audioContextRef.current = new AudioContextClass();
                }
                const context = audioContextRef.current;
                await context.resume?.();
                const startAt = context.currentTime + 0.06;

                round.pattern.forEach((tone, index) => {
                    const oscillator = context.createOscillator();
                    const gain = context.createGain();
                    const toneStart = startAt + index * 0.36;
                    oscillator.type = 'sine';
                    oscillator.frequency.setValueAtTime(TONE_FREQUENCIES[tone], toneStart);
                    gain.gain.setValueAtTime(0.0001, toneStart);
                    gain.gain.exponentialRampToValueAtTime(0.18, toneStart + 0.025);
                    gain.gain.exponentialRampToValueAtTime(0.0001, toneStart + 0.25);
                    oscillator.connect(gain);
                    gain.connect(context.destination);
                    oscillator.start(toneStart);
                    oscillator.stop(toneStart + 0.27);
                });
            } catch (error) {
                audioContextRef.current?.close?.();
                audioContextRef.current = null;
            }
        }

    };

    const choose = (pattern) => {
        if (stage !== 'answer' || choice) {
            return;
        }

        const key = patternKey(pattern);
        const isCorrect = key === patternKey(round.pattern);
        const nextStreak = isCorrect ? streak + 1 : 0;
        const shouldLevelUp = isCorrect && nextStreak > 0 && nextStreak % 3 === 0;
        const nextLevel = normalizeBrainGameLevel('tone-order', level + (isCorrect ? (shouldLevelUp ? 1 : 0) : -1));
        setChoice(key);
        setScore((current) => current + (isCorrect ? 1 : 0));
        setStreak(nextStreak);
        setLevel(nextLevel);
        setStage('feedback');

        timerRef.current = window.setTimeout(() => {
            setRound(createToneRound(nextLevel));
            setChoice('');
            setPlayCount(0);
            setStage('ready');
        }, isCorrect ? 700 : 950);
    };

    return (
        <GameShell
            {...props}
            copy={copy}
            metrics={{ level, score, streak }}
            tone={correct ? 'good' : choice ? 'miss' : 'ready'}
        >
            <div className="UFOV-brain-games__tone-game">
                <button
                    aria-label={stage === 'answer' ? copy.replayTones : copy.playTones}
                    className="UFOV-brain-games__tone-play"
                    disabled={stage === 'playing' || stage === 'feedback' || (stage === 'answer' && playCount >= 2)}
                    onClick={playPattern}
                    type="button"
                >
                    <BrainTrainingGameIcon icon={stage === 'playing' ? 'repeat' : 'play'} />
                </button>
                <div className="UFOV-brain-games__game-cue">{copy.toneOrderCue}</div>
                <div className="UFOV-brain-games__tone-options">
                    {round.options.map((pattern) => {
                        const key = patternKey(pattern);
                        const picked = choice === key;
                        const answer = choice && key === patternKey(round.pattern);
                        return (
                            <button
                                aria-label={formatGameCopy(copy.toneOrder, {
                                    order: pattern.map((tone) => tone + 1).join(' '),
                                })}
                                className={`${picked ? 'is-picked' : ''} ${answer ? 'is-correct' : ''}`.trim()}
                                disabled={stage !== 'answer'}
                                key={key}
                                onClick={() => choose(pattern)}
                                type="button"
                            >
                                <TonePattern pattern={pattern} />
                            </button>
                        );
                    })}
                </div>
            </div>
        </GameShell>
    );
}

export default ToneOrderGame;
